"use client";

import { useState, useEffect, ReactNode } from "react";
import { useRouter } from "next/navigation";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertTriangle, CreditCard, Clock, Loader2, Check, XCircle } from "lucide-react";
import { useTranslations } from "@/hooks/useTranslations";

interface SubscriptionData {
  subscriptionStatus: "ACTIVE" | "TRIAL" | "PAST_DUE" | "CANCELLED" | null;
  trialEndsAt: string | null;
  currentPeriodEnd: string | null;
}

interface SubscriptionGuardProps {
  children: ReactNode;
}

const PRICE_MONTHLY = 99;

export function SubscriptionGuard({ children }: SubscriptionGuardProps) {
  const router = useRouter();
  const t = useTranslations("guards.subscription");
  const [subscription, setSubscription] = useState<SubscriptionData | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [subscribeError, setSubscribeError] = useState<string | null>(null);
  const [fetchError, setFetchError] = useState<string | null>(null);

  const features = [
    t("features.unlimitedJobs"),
    t("features.unlimitedQuotes"),
    t("features.unlimitedTeam"),
    t("features.clientPortal"),
    t("features.whiteLabelBranding"),
    t("features.analyticsReporting"),
  ];

  useEffect(() => {
    async function checkSubscription() {
      try {
        const response = await fetch("/api/organization/current");
        if (!response.ok) {
          // Fail-closed: if we can't verify subscription, block access
          setFetchError(t("unableToVerify"));
          setLoading(false);
          return;
        }
        const data = await response.json();
        setSubscription({
          subscriptionStatus: data.subscriptionStatus || null,
          trialEndsAt: data.trialEndsAt,
          currentPeriodEnd: data.currentPeriodEnd,
        });
      } catch (error) {
        console.error("Error checking subscription:", error);
        // Fail-closed: block access on error
        setFetchError(t("unableToVerify"));
      } finally {
        setLoading(false);
      }
    }

    checkSubscription();
  }, [t]);

  // Handle Subscribe button click
  async function handleSubscribe() {
    setActionLoading(true);
    setSubscribeError(null);
    try {
      const response = await fetch("/api/stripe/create-checkout", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ mode: "purchase" }),
      });

      const data = await response.json();

      if (!response.ok) {
        // Show the actual error message from the API
        throw new Error(data.error || "Failed to create checkout session");
      }

      if (data.data?.url) {
        window.location.href = data.data.url;
      } else if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error("No checkout URL returned. Please check your Stripe configuration.");
      }
    } catch (error) {
      console.error("Error creating checkout session:", error);
      setSubscribeError(error instanceof Error ? error.message : "An unexpected error occurred");
      setActionLoading(false);
    }
  }

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto text-gray-400" />
          <p className="mt-2 text-gray-500">{t("loading")}</p>
        </div>
      </div>
    );
  }

  // Fail-closed: if we couldn't fetch subscription data, show error
  if (fetchError) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 p-3 rounded-full bg-error/10 w-fit">
              <XCircle className="h-8 w-8 text-error" />
            </div>
            <CardTitle className="text-xl">{t("connectionError")}</CardTitle>
            <CardDescription className="text-base">
              {fetchError}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button
              onClick={() => window.location.reload()}
              className="w-full"
              size="lg"
            >
              {t("retry")}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Check if subscription is valid
  if (subscription) {
    const { subscriptionStatus, trialEndsAt } = subscription;

    // Active subscription - allow access
    if (subscriptionStatus === "ACTIVE") {
      return <>{children}</>;
    }

    // Trial - check if expired
    if (subscriptionStatus === "TRIAL" && trialEndsAt) {
      const trialEnd = new Date(trialEndsAt);
      const now = new Date();

      if (now < trialEnd) {
        // Trial still active - allow access
        return <>{children}</>;
      }

      // Trial expired - show upgrade screen
      const daysExpired = Math.floor((now.getTime() - trialEnd.getTime()) / (1000 * 60 * 60 * 24));

      const getExpiredText = () => {
        if (daysExpired === 0) return t("trialEnded.today");
        if (daysExpired === 1) return t("trialEnded.daysAgo", { count: daysExpired });
        return t("trialEnded.daysAgoPlural", { count: daysExpired });
      };

      return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-4">
          <Card className="max-w-lg w-full border-warning">
            <CardHeader className="text-center">
              <div className="mx-auto mb-4 p-3 rounded-full bg-warning/10 w-fit">
                <Clock className="h-8 w-8 text-warning" />
              </div>
              <CardTitle className="text-2xl">{t("trialEnded.title")}</CardTitle>
              <CardDescription className="text-base">
                {t("trialEnded.description", { days: getExpiredText() })}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Price */}
              <div className="text-center py-4 bg-gray-100 dark:bg-gray-800 rounded-lg">
                <div className="flex items-baseline justify-center gap-1">
                  <span className="text-4xl font-bold">${PRICE_MONTHLY}</span>
                  <span className="text-gray-500">{t("pricing.perMonth")}</span>
                </div>
                <p className="text-sm text-gray-500 mt-1">{t("pricing.unlimitedTeam")}</p>
              </div>

              {/* Features */}
              <div className="space-y-2">
                {features.map((feature, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm">
                    <Check className="h-4 w-4 text-success flex-shrink-0" />
                    <span>{feature}</span>
                  </div>
                ))}
              </div>

              {/* Error Message */}
              {subscribeError && (
                <Alert variant="destructive">
                  <XCircle className="h-4 w-4" />
                  <AlertDescription>{subscribeError}</AlertDescription>
                </Alert>
              )}

              {/* CTA */}
              <Button
                onClick={handleSubscribe}
                disabled={actionLoading}
                className="w-full"
                size="lg"
              >
                {actionLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {t("buttons.subscribeNow", { price: PRICE_MONTHLY })}
              </Button>

              <p className="text-xs text-center text-gray-500">
                {t("footer.dataSafe")}
              </p>
            </CardContent>
          </Card>
        </div>
      );
    }

    // Cancelled or past due - show upgrade screen
    if (subscriptionStatus === "CANCELLED" || subscriptionStatus === "PAST_DUE") {
      return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-4">
          <Card className="max-w-lg w-full border-error">
            <CardHeader className="text-center">
              <div className="mx-auto mb-4 p-3 rounded-full bg-error/10 w-fit">
                <AlertTriangle className="h-8 w-8 text-error" />
              </div>
              <CardTitle className="text-2xl">
                {subscriptionStatus === "PAST_DUE" ? t("paymentFailed.title") : t("subscriptionRequired.title")}
              </CardTitle>
              <CardDescription className="text-base">
                {subscriptionStatus === "PAST_DUE"
                  ? t("paymentFailed.description")
                  : t("subscriptionRequired.description")}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Price */}
              <div className="text-center py-4 bg-gray-100 dark:bg-gray-800 rounded-lg">
                <div className="flex items-baseline justify-center gap-1">
                  <span className="text-4xl font-bold">${PRICE_MONTHLY}</span>
                  <span className="text-gray-500">{t("pricing.perMonth")}</span>
                </div>
                <p className="text-sm text-gray-500 mt-1">{t("pricing.unlimitedTeam")}</p>
              </div>

              {/* Features */}
              <div className="space-y-2">
                {features.map((feature, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm">
                    <Check className="h-4 w-4 text-success flex-shrink-0" />
                    <span>{feature}</span>
                  </div>
                ))}
              </div>

              {/* Error Message */}
              {subscribeError && (
                <Alert variant="destructive">
                  <XCircle className="h-4 w-4" />
                  <AlertDescription>{subscribeError}</AlertDescription>
                </Alert>
              )}

              {/* CTA */}
              <Button
                onClick={handleSubscribe}
                disabled={actionLoading}
                className="w-full"
                size="lg"
              >
                {actionLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {subscriptionStatus === "PAST_DUE" ? t("buttons.updatePayment") : t("buttons.subscribeNow", { price: PRICE_MONTHLY })}
              </Button>

              <p className="text-xs text-center text-gray-500">
                {t("footer.dataSafe")}
              </p>
            </CardContent>
          </Card>
        </div>
      );
    }
  }

  // Default: No valid subscription - show subscribe screen
  // This handles: null subscription, null subscriptionStatus, or any unrecognized status
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-4">
      <Card className="max-w-lg w-full border-primary">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 p-3 rounded-full bg-primary/10 w-fit">
            <CreditCard className="h-8 w-8 text-primary" />
          </div>
          <CardTitle className="text-2xl">{t("subscribe.title")}</CardTitle>
          <CardDescription className="text-base">
            {t("subscribe.description")}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Price */}
          <div className="text-center py-4 bg-gray-100 dark:bg-gray-800 rounded-lg">
            <div className="flex items-baseline justify-center gap-1">
              <span className="text-4xl font-bold">${PRICE_MONTHLY}</span>
              <span className="text-gray-500">{t("pricing.perMonth")}</span>
            </div>
            <p className="text-sm text-gray-500 mt-1">{t("pricing.unlimitedTeam")}</p>
          </div>

          {/* Features */}
          <div className="space-y-2">
            {features.map((feature, i) => (
              <div key={i} className="flex items-center gap-2 text-sm">
                <Check className="h-4 w-4 text-success flex-shrink-0" />
                <span>{feature}</span>
              </div>
            ))}
          </div>

          {/* Error Message */}
          {subscribeError && (
            <Alert variant="destructive">
              <XCircle className="h-4 w-4" />
              <AlertDescription>{subscribeError}</AlertDescription>
            </Alert>
          )}

          {/* CTA */}
          <Button
            onClick={handleSubscribe}
            disabled={actionLoading}
            className="w-full"
            size="lg"
          >
            {actionLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {t("buttons.subscribeNow", { price: PRICE_MONTHLY })}
          </Button>

          <p className="text-xs text-center text-gray-500">
            {t("footer.startManaging")}
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
